/**
 * Outil « Lanceur » (Boîte à outils, 6e → 1re).
 *
 * Pièce, dé, roue à trois secteurs réglables : on lance 1, 10, 100, 1 000
 * ou 10 000 fois, le tableau des effectifs et des fréquences se remplit à
 * côté des probabilités exactes, et la courbe de la fréquence d'une issue se
 * stabilise autour de sa probabilité (bande p ± 1/√n à partir de la 2nde).
 * « Dé mystère » : équilibré ou truqué ? On lance, on tranche, on vérifie.
 * Calculs : `launcher.ts` ; tirages : `ui/random.ts`.
 */

import { useMemo, useState } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { Note, PRIMARY_BUTTON, SECONDARY_BUTTON, Segmented, Stepper } from '../../ui/controls';
import { formatNumber, numberAtoms } from '../../ui/format';
import { fluctuationInterval, logSampleIndices } from '../../ui/random';
import { AXIS, FIGURE_BG_CLASS, GRID, HaloText } from '../../ui/svg-parts';
import {
  BATCHES,
  emptyThrows,
  FLUCTUATION_MIN,
  fraction,
  issuesOf,
  MAX_THROWS,
  mysteryEvidence,
  MYSTERY_ENOUGH,
  pickMystery,
  probabilities,
  runningFrequency,
  throwMany,
  WHEEL_COLORS,
  type ExperimentId,
  type Mystery,
  type Throws,
} from './launcher';

const EXPERIMENTS: ReadonlyArray<{ value: ExperimentId; label: string }> = [
  { value: 'coin', label: 'Pièce' },
  { value: 'die', label: 'Dé' },
  { value: 'wheel', label: 'Roue' },
  { value: 'mystery', label: 'Dé mystère' },
];

const W = 320;
const LINE = 'var(--color-primary-500)';
const PROBA = 'var(--color-xp-500)';

/** « 1 lancer », « 1 000 lancers » */
const throwsWord = (n: number) => `${count(n)} lancer${n > 1 ? 's' : ''}`;
const count = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const freq = (v: number) => formatNumber(v, 3);

export function RandomLauncherTool({
  onSend,
  sendCost,
  level,
  rng = Math.random,
}: ToolProps & { level?: string | null; rng?: () => number }) {
  const rank = schoolLevelRank(level);
  const showBand = rank === null || rank >= 5;
  const [exp, setExp] = useState<ExperimentId>('die');
  const [wheel, setWheel] = useState<number[]>([3, 2, 1]);
  const [mystery, setMystery] = useState<Mystery>(() => pickMystery(rng));
  const [guess, setGuess] = useState<boolean | null>(null);
  const issues = useMemo(() => issuesOf(exp, wheel, mystery), [exp, wheel, mystery]);
  const probs = useMemo(() => probabilities(issues), [issues]);
  const [throws, setThrows] = useState<Throws>(() => emptyThrows(6));
  const [tracked, setTracked] = useState(0);
  const isMystery = exp === 'mystery';
  const revealed = !isMystery || guess !== null;

  const reset = (issueCount: number) => {
    setThrows(emptyThrows(issueCount));
    setTracked(0);
  };
  const switchExp = (e: ExperimentId) => {
    setExp(e);
    setGuess(null);
    if (e === 'mystery') setMystery(pickMystery(rng));
    reset(e === 'coin' ? 2 : e === 'wheel' ? 3 : 6);
  };
  const setSector = (i: number) => (v: number) => {
    setWheel((w) => w.map((x, j) => (j === i ? v : x)));
    reset(3);
  };
  const launch = (k: number) => setThrows((t) => throwMany(t, issues, k, rng));

  const last = throws.n > 0 ? throws.outcomes[throws.n - 1]! : null;
  const full = throws.n >= MAX_THROWS;
  const p = probs[tracked]!;
  const fTracked = throws.n > 0 ? throws.counts[tracked]! / throws.n : null;
  const totalWeight = issues.reduce((s2, i) => s2 + i.weight, 0);
  const contrary = fraction(totalWeight - issues[tracked]!.weight, totalWeight);

  return (
    <div className="space-y-4">
      <p className="text-ink-600 text-sm">
        Lance beaucoup de fois : la fréquence de chaque issue se rapproche de sa probabilité.
      </p>
      <Segmented ariaLabel="Expérience" value={exp} options={EXPERIMENTS} onChange={switchExp} />

      {exp === 'wheel' && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {WHEEL_COLORS.map((c, i) => (
            <Stepper
              key={c.label}
              label={c.label}
              value={wheel[i]!}
              min={1}
              max={6}
              onChange={setSector(i)}
              format={(v) => `${v} part${v > 1 ? 's' : ''}`}
            />
          ))}
        </div>
      )}
      {isMystery && (
        <Note live={false}>
          Ce dé est peut-être truqué : une face aurait alors 1 chance sur 3 de sortir. Lance-le
          autant que tu veux, puis décide.
        </Note>
      )}

      <div className="flex items-center gap-4">
        <OutcomeVisual exp={exp} wheel={wheel} last={last} />
        <div className="min-w-0 flex-1">
          <p
            className="text-ink-900 text-base font-semibold"
            aria-live="polite"
            data-testid="lr-last"
          >
            {last === null ? 'Pas encore de lancer.' : `Dernier lancer : ${issues[last]!.label}`}
          </p>
          <p className="text-ink-500 text-sm" data-testid="lr-total">
            {throwsWord(throws.n)}
            {full && ' (maximum atteint)'}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Lancer">
        {BATCHES.map((k) => (
          <button
            key={k}
            type="button"
            disabled={full}
            onClick={() => launch(k)}
            aria-label={`Lancer ${k} fois`}
            className={cn(k === 1 ? PRIMARY_BUTTON : SECONDARY_BUTTON, 'disabled:opacity-40')}
          >
            {k === 1 ? 'Lancer' : `× ${count(k)}`}
          </button>
        ))}
        <button
          type="button"
          onClick={() => reset(issues.length)}
          disabled={throws.n === 0}
          className={cn(SECONDARY_BUTTON, 'disabled:opacity-40')}
        >
          Effacer
        </button>
      </div>

      <table className="w-full text-sm" data-testid="lr-table">
        <caption className="sr-only">Effectifs, fréquences et probabilités</caption>
        <thead>
          <tr className="text-ink-500 text-left text-xs">
            <th className="py-1 font-semibold">Issue</th>
            <th className="py-1 text-right font-semibold">Effectif</th>
            <th className="py-1 text-right font-semibold">Fréquence</th>
            {revealed && <th className="py-1 text-right font-semibold">Probabilité</th>}
          </tr>
        </thead>
        <tbody>
          {issues.map((iss, i) => (
            <tr
              key={iss.label}
              className={cn('border-ink-100 border-t', tracked === i && 'bg-primary-50')}
            >
              <td className="py-0.5">
                <button
                  type="button"
                  aria-pressed={tracked === i}
                  aria-label={`Suivre la fréquence de ${iss.label}`}
                  onClick={() => setTracked(i)}
                  className="text-ink-900 flex min-h-11 w-full items-center gap-2 text-left font-semibold"
                >
                  <span
                    className="h-3 w-3 shrink-0 rounded-full"
                    style={{ background: iss.color }}
                    aria-hidden
                  />
                  {iss.label}
                </button>
              </td>
              <td className="text-ink-800 text-right tabular-nums">
                {count(throws.counts[i] ?? 0)}
              </td>
              <td className="text-ink-800 text-right tabular-nums">
                {throws.n > 0 ? freq((throws.counts[i] ?? 0) / throws.n) : '—'}
              </td>
              {revealed && (
                <td className="text-ink-800 text-right tabular-nums">
                  {probs[i]!.plain}
                  {probs[i]!.plain.includes('/') && (
                    <span className="text-ink-500">
                      {' '}
                      {probs[i]!.decimal ? `= ${probs[i]!.decimal}` : `≈ ${freq(probs[i]!.value)}`}
                    </span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {revealed && p.plain !== '1' && (
        <p className="text-ink-700 text-sm" data-testid="lr-contrary">
          Événement contraire « non {issues[tracked]!.label} » : probabilité 1 − {p.plain} ={' '}
          {contrary.plain}
          {fTracked !== null && ` ; fréquence ${freq(1 - fTracked)}`}.
        </p>
      )}

      <BarsChart
        issues={issues.map((i) => i.label)}
        colors={issues.map((i) => i.color)}
        freqs={issues.map((_, i) => (throws.n > 0 ? (throws.counts[i] ?? 0) / throws.n : 0))}
        probs={revealed ? probs.map((x) => x.value) : null}
      />

      <section className="space-y-2" aria-label="Fréquence au fil des lancers">
        <p className="text-ink-700 text-sm font-semibold">
          Fréquence de « {issues[tracked]!.label} » au fil des lancers
        </p>
        <RunningChart
          throws={throws}
          tracked={tracked}
          p={revealed ? p.value : null}
          band={showBand && revealed}
        />
        <p className="text-ink-500 text-xs">
          Nombre de lancers en échelle logarithmique : 1, 10, 100, 1 000 sont également espacés.
          {showBand &&
            revealed &&
            ' Bande verte : p ± 1/√n. Pour n ≥ 25, dans au moins 95 % des séries de n lancers, la fréquence y tombe.'}
        </p>
        {throws.n > 0 && fTracked !== null && (
          <div className="space-y-2">
            <p className="text-ink-800 text-sm" data-testid="lr-tracked">
              Après {throwsWord(throws.n)} : f = {freq(fTracked)}
              {revealed && ` ; p = ${p.plain}`}
              {revealed && showBand && throws.n >= FLUCTUATION_MIN && (
                <>
                  {' '}
                  ; p ± 1/√n = [{freq(fluctuationInterval(p.value, throws.n)[0])} ;{' '}
                  {freq(fluctuationInterval(p.value, throws.n)[1])}]
                </>
              )}
            </p>
            {onSend && (
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {revealed && (
                  <span className="text-ink-700 flex items-center gap-2 text-sm">
                    p = {p.plain}
                    <SendToSheetButton
                      label={p.plain}
                      cost={sendCost}
                      onClick={() => onSend({ atoms: p.atoms })}
                    />
                  </span>
                )}
                <span className="text-ink-700 flex items-center gap-2 text-sm">
                  f = {freq(fTracked)}
                  <SendToSheetButton
                    label={freq(fTracked)}
                    cost={sendCost}
                    onClick={() => onSend({ atoms: numberAtoms(fTracked, 3) })}
                  />
                </span>
              </div>
            )}
          </div>
        )}
      </section>

      {isMystery && (
        <MysteryPanel
          throws={throws}
          mystery={mystery}
          guess={guess}
          showBand={showBand}
          onGuess={setGuess}
          onAgain={() => switchExp('mystery')}
        />
      )}
    </div>
  );
}

function MysteryPanel({
  throws,
  mystery,
  guess,
  showBand,
  onGuess,
  onAgain,
}: {
  throws: Throws;
  mystery: Mystery;
  guess: boolean | null;
  showBand: boolean;
  onGuess: (loaded: boolean) => void;
  onAgain: () => void;
}) {
  const ev = mysteryEvidence(throws);
  if (guess === null) {
    return (
      <section className="border-ink-100 space-y-2 rounded-2xl border p-3" data-testid="lr-mystery">
        <p className="text-ink-900 text-sm font-semibold">Ton verdict ?</p>
        {throws.n > 0 && throws.n < MYSTERY_ENOUGH && (
          <p className="text-ink-500 text-sm">
            Avec {throwsWord(throws.n)} seulement, le hasard peut encore te tromper.
          </p>
        )}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            className={SECONDARY_BUTTON}
            disabled={throws.n === 0}
            onClick={() => onGuess(false)}
          >
            Équilibré
          </button>
          <button
            type="button"
            className={SECONDARY_BUTTON}
            disabled={throws.n === 0}
            onClick={() => onGuess(true)}
          >
            Truqué
          </button>
        </div>
      </section>
    );
  }
  const right = guess === mystery.loaded;
  return (
    <section className="space-y-2" data-testid="lr-mystery">
      <Note tone={right ? 'success' : 'error'}>
        {right ? 'Bien vu : ' : 'Raté : '}
        {mystery.loaded
          ? `le dé était truqué, le ${mystery.face} avait 1 chance sur 3 de sortir.`
          : 'le dé était équilibré, chaque face avait 1 chance sur 6 de sortir.'}
        {ev && (
          <>
            {' '}
            Sur {throwsWord(throws.n)}, la face la plus fréquente est le {ev.face} (f ={' '}
            {freq(ev.freq)}, contre 1/6 ≈ 0,167 pour un dé équilibré)
            {!showBand
              ? '.'
              : !ev.usable
                ? ` : trop peu de lancers pour utiliser 1/6 ± 1/√n (il en faut au moins ${FLUCTUATION_MIN}).`
                : ev.outside
                  ? `, hors de 1/6 ± 1/√n = [${freq(ev.interval[0])} ; ${freq(ev.interval[1])}] : c’est un signe fort de trucage.`
                  : `, dans 1/6 ± 1/√n = [${freq(ev.interval[0])} ; ${freq(ev.interval[1])}] : écart compatible avec un dé équilibré, mais on ne peut pas conclure qu’il l’est. Lance davantage.`}
          </>
        )}
      </Note>
      <button type="button" className={PRIMARY_BUTTON} onClick={onAgain}>
        Un autre dé mystère
      </button>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Figures

function OutcomeVisual({
  exp,
  wheel,
  last,
}: {
  exp: ExperimentId;
  wheel: readonly number[];
  last: number | null;
}) {
  const size = 88;
  if (exp === 'coin') {
    return (
      <svg width={size} height={size} viewBox="0 0 88 88" aria-hidden className="shrink-0">
        <circle
          cx={44}
          cy={44}
          r={40}
          fill="var(--color-accent-100)"
          stroke="var(--color-accent-500)"
          strokeWidth={3}
        />
        <text
          x={44}
          y={50}
          textAnchor="middle"
          fontSize={18}
          fontWeight={700}
          fill="var(--color-ink-800)"
        >
          {last === null ? '?' : last === 0 ? 'Pile' : 'Face'}
        </text>
      </svg>
    );
  }
  if (exp === 'wheel') {
    const total = wheel.reduce((s, w) => s + w, 0);
    let a0 = -Math.PI / 2;
    // la roue tourne pour amener le secteur tiré sous l'aiguille (en haut)
    const before = last === null ? 0 : wheel.slice(0, last).reduce((s, w) => s + w, 0);
    const turn = last === null ? 0 : ((before + wheel[last]! / 2) / total) * 360;
    return (
      <svg width={size} height={size} viewBox="0 0 88 88" aria-hidden className="shrink-0">
        <g transform={`rotate(${-turn.toFixed(1)} 44 46)`} data-testid="lr-wheel">
          {wheel.map((w, i) => {
            const a1 = a0 + (2 * Math.PI * w) / total;
            const large = a1 - a0 > Math.PI ? 1 : 0;
            const d = `M44,46 L${44 + 38 * Math.cos(a0)},${46 + 38 * Math.sin(a0)} A38,38 0 ${large} 1 ${44 + 38 * Math.cos(a1)},${46 + 38 * Math.sin(a1)} Z`;
            a0 = a1;
            return (
              <path
                key={i}
                d={d}
                fill={WHEEL_COLORS[i]!.color}
                fillOpacity={last === null || last === i ? 0.9 : 0.3}
                stroke="var(--fig-bg, #fff)"
                strokeWidth={2}
              />
            );
          })}
        </g>
        <path d="M38,1 L50,1 L44,12 Z" fill="var(--color-ink-800)" />
      </svg>
    );
  }
  // dé : faces à points
  const face = last === null ? null : last + 1;
  const pips: Record<number, Array<[number, number]>> = {
    1: [[44, 44]],
    2: [
      [28, 28],
      [60, 60],
    ],
    3: [
      [28, 28],
      [44, 44],
      [60, 60],
    ],
    4: [
      [28, 28],
      [60, 28],
      [28, 60],
      [60, 60],
    ],
    5: [
      [28, 28],
      [60, 28],
      [44, 44],
      [28, 60],
      [60, 60],
    ],
    6: [
      [28, 26],
      [60, 26],
      [28, 44],
      [60, 44],
      [28, 62],
      [60, 62],
    ],
  };
  return (
    <svg width={size} height={size} viewBox="0 0 88 88" aria-hidden className="shrink-0">
      <rect
        x={6}
        y={6}
        width={76}
        height={76}
        rx={16}
        fill="var(--fig-bg, #fff)"
        stroke="var(--color-ink-700)"
        strokeWidth={3}
      />
      {face === null ? (
        <text
          x={44}
          y={52}
          textAnchor="middle"
          fontSize={24}
          fontWeight={700}
          fill="var(--color-ink-500)"
        >
          ?
        </text>
      ) : (
        pips[face]!.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={6} fill="var(--color-ink-800)" />
        ))
      )}
    </svg>
  );
}

function BarsChart({
  issues,
  colors,
  freqs,
  probs,
}: {
  issues: readonly string[];
  colors: readonly string[];
  freqs: readonly number[];
  probs: readonly number[] | null;
}) {
  const H = 150;
  const top = Math.max(0.2, ...freqs, ...(probs ?? [])) * 1.1;
  const slot = W / issues.length;
  const y = (v: number) => H - 22 - (v / top) * (H - 34);
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={cn('border-ink-100 block w-full rounded-2xl border', FIGURE_BG_CLASS)}
      role="img"
      aria-label={`Fréquences observées${probs ? ' et probabilités (traits pointillés)' : ''}.`}
      data-testid="lr-bars"
    >
      <line x1={0} y1={y(0)} x2={W} y2={y(0)} stroke={AXIS} strokeWidth={1.25} />
      {issues.map((label, i) => {
        const x = i * slot + slot * 0.2;
        const w = slot * 0.6;
        return (
          <g key={label}>
            <rect
              x={x}
              y={y(freqs[i]!)}
              width={w}
              height={y(0) - y(freqs[i]!)}
              fill={colors[i]}
              fillOpacity={0.75}
              rx={3}
            />
            {probs && (
              <line
                x1={x - 4}
                x2={x + w + 4}
                y1={y(probs[i]!)}
                y2={y(probs[i]!)}
                stroke={PROBA}
                strokeWidth={2.5}
                strokeDasharray="5 3"
              />
            )}
            <text
              x={x + w / 2}
              y={H - 6}
              textAnchor="middle"
              fontSize={12}
              fill="var(--color-ink-600)"
            >
              {label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function RunningChart({
  throws,
  tracked,
  p,
  band,
}: {
  throws: Throws;
  tracked: number;
  p: number | null;
  band: boolean;
}) {
  const H = 190;
  const idx = useMemo(() => logSampleIndices(Math.max(1, throws.n)), [throws.n]);
  const values = useMemo(
    () => (throws.n > 0 ? runningFrequency(throws, tracked, idx) : []),
    [throws, tracked, idx],
  );
  const nMax = Math.max(10, throws.n);
  const lx = (n: number) => 8 + (Math.log10(n) / Math.log10(nMax)) * (W - 16);
  const yMax = 1;
  const ly = (v: number) => H - 20 - (v / yMax) * (H - 30);
  const path = values
    .map((v, i) => `${i ? 'L' : 'M'}${lx(idx[i]!).toFixed(1)},${ly(v).toFixed(1)}`)
    .join('');
  const decades = [1, 10, 100, 1000, 10000, 100000].filter((d) => d <= nMax);
  let bandPath = '';
  if (p !== null && band) {
    const ns = logSampleIndices(nMax, 80).filter((n) => n >= FLUCTUATION_MIN);
    const upper = ns.map(
      (n) => `${lx(n).toFixed(1)},${ly(Math.min(1, p + 1 / Math.sqrt(n))).toFixed(1)}`,
    );
    const lower = ns
      .map((n) => `${lx(n).toFixed(1)},${ly(Math.max(0, p - 1 / Math.sqrt(n))).toFixed(1)}`)
      .reverse();
    if (ns.length > 1) bandPath = `M${upper.join('L')}L${lower.join('L')}Z`;
  }
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={cn('border-ink-100 block w-full rounded-2xl border', FIGURE_BG_CLASS)}
      role="img"
      aria-label="Fréquence de l’issue suivie en fonction du nombre de lancers (échelle logarithmique)."
      data-testid="lr-running"
    >
      {[0.25, 0.5, 0.75, 1].map((v) => (
        <g key={v}>
          <line x1={0} x2={W} y1={ly(v)} y2={ly(v)} stroke={GRID} />
          {v < 1 && (
            <text x={12} y={ly(v) - 3} fontSize={12} fill="var(--color-ink-500)">
              {formatNumber(v, 2)}
            </text>
          )}
        </g>
      ))}
      {decades.map((d) => (
        <g key={d}>
          <line x1={lx(d)} x2={lx(d)} y1={0} y2={H - 20} stroke={GRID} />
          <text
            x={lx(d)}
            y={H - 6}
            textAnchor={lx(d) > W - 24 ? 'end' : lx(d) < 24 ? 'start' : 'middle'}
            fontSize={12}
            fill="var(--color-ink-500)"
          >
            {d >= 1000 ? count(d) : d}
          </text>
        </g>
      ))}
      {bandPath && <path d={bandPath} fill={PROBA} fillOpacity={0.15} data-testid="lr-band" />}
      {p !== null && (
        <g>
          <line
            x1={0}
            x2={W}
            y1={ly(p)}
            y2={ly(p)}
            stroke={PROBA}
            strokeWidth={2}
            strokeDasharray="7 5"
          />
          <HaloText
            x={W - 12}
            y={ly(p) - 5}
            textAnchor="end"
            fontSize={12}
            fontWeight={700}
            fill={PROBA}
          >
            p
          </HaloText>
        </g>
      )}
      {path && (
        <path d={path} fill="none" stroke={LINE} strokeWidth={2.25} strokeLinejoin="round" />
      )}
    </svg>
  );
}
